import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface SynonymRow {
  readonly searchSynonymId: string;
  readonly fromTerm: string;
  readonly toTerm: string;
  readonly synonymType: string;
  readonly isBidirectional: boolean;
  readonly languageCode: string;
  readonly weight: string;
  readonly isActive: boolean;
}

const SYNONYM_TYPES = ['SYNONYM', 'ACRONYM', 'ABBREVIATION', 'SPELLING'];

/**
 * M39 — Search Synonym Console (GR_M36-M40 §L39.03 / §M rows 17-18). The admin
 * surface over the query-expansion synonym dictionary (T522C): search
 * administrators author the from → to term mappings that grow a query into the set
 * of terms it should also match (honouring is_bidirectional), so recall is
 * DB-tunable (LAW 6) rather than hard-coded. Viewing needs SYNONYM_VIEW; authoring
 * / deleting needs SYNONYM_MANAGE (SoD). Soft-delete only (LAW 3); tenant-isolated
 * server-side (LAW 4). This screen backs the /admin/search/synonyms route.
 */
export function SearchSynonymsConsole(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<SynonymRow[]>([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [fromTerm, setFromTerm] = useState('');
  const [toTerm, setToTerm] = useState('');
  const [synonymType, setSynonymType] = useState('SYNONYM');
  const [isBidirectional, setIsBidirectional] = useState(true);
  const [languageCode, setLanguageCode] = useState('en');
  const [weight, setWeight] = useState('1.0');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('synonymType', typeFilter);
    apiRequest<{ data: SynonymRow[] }>(`/v1/search/synonyms?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    if (!fromTerm.trim() || !toTerm.trim()) {
      setFormError(t('m39.synonyms.requiredFields'));
      return;
    }
    setBusy(true);
    try {
      await apiRequest<{ data: SynonymRow }>('/v1/search/synonyms', {
        method: 'POST',
        body: {
          fromTerm: fromTerm.trim(),
          toTerm: toTerm.trim(),
          synonymType,
          isBidirectional,
          languageCode: languageCode.trim() || undefined,
          weight: Number(weight) || 1.0,
          description: description.trim() || undefined,
        },
        token,
      });
      setCreated(t('m39.synonyms.created', { from: fromTerm.trim(), to: toTerm.trim() }));
      setFromTerm(''); setToTerm(''); setDescription(''); setWeight('1.0');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setFormError(t('m39.synonyms.notAuthorised'));
      } else {
        setFormError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (r: SynonymRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/synonyms/${r.searchSynonymId}`, {
        method: 'PATCH',
        body: { isActive: !r.isActive },
        token,
      });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (r: SynonymRow): Promise<void> => {
    try {
      await apiRequest(`/v1/search/synonyms/${r.searchSynonymId}`, { method: 'DELETE', token });
      setReload((n) => n + 1);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <main aria-label="search-synonyms-console">
      <h1>{t('m39.synonyms.title')}</h1>
      <p>{t('m39.synonyms.intro')}</p>

      <section aria-label="synonym-create">
        <h2>{t('m39.synonyms.newSynonym')}</h2>
        <form onSubmit={submit} aria-label="synonym-form">
          <label>{t('m39.synonyms.fromTerm')}
            <input aria-label="from-term" value={fromTerm} onChange={(e) => setFromTerm(e.target.value)} />
          </label>
          <label>{t('m39.synonyms.toTerm')}
            <input aria-label="to-term" value={toTerm} onChange={(e) => setToTerm(e.target.value)} />
          </label>
          <label>{t('m39.synonyms.synonymType')}
            <select aria-label="synonym-type" value={synonymType} onChange={(e) => setSynonymType(e.target.value)}>
              {SYNONYM_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m39.synonyms.bidirectional')}
            <input aria-label="bidirectional" type="checkbox" checked={isBidirectional} onChange={(e) => setIsBidirectional(e.target.checked)} />
          </label>
          <label>{t('m39.synonyms.languageCode')}
            <input aria-label="language-code" value={languageCode} onChange={(e) => setLanguageCode(e.target.value)} />
          </label>
          <label>{t('m39.synonyms.weight')}
            <input aria-label="weight" type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} />
          </label>
          <label>{t('m39.common.description')}
            <input aria-label="description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <Button variant="primary" type="submit" disabled={busy}>{busy ? t('m39.common.saving') : t('m39.synonyms.createSynonym')}</Button>
        </form>
        {formError && <p role="alert">{formError}</p>}
        {created && <p aria-label="create-success">{created}</p>}
      </section>

      <section aria-label="synonym-filters">
        <label>{t('m39.synonyms.filterByType')}
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">{t('m39.synonyms.all')}</option>
            {SYNONYM_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading
        ? <p>{t('common.loading')}</p>
        : (
          <DataTable<SynonymRow>
            ariaLabel="synonym-table"
            testId="synonym-table"
            rowKey={(r) => r.searchSynonymId}
            rowAriaLabel={(r) => `synonym-${r.fromTerm}`}
            emptyMessage={t('m39.synonyms.empty')}
            rows={rows}
            columns={[
              { key: 'from', header: t('m39.synonyms.colFrom'), render: (r) => r.fromTerm },
              { key: 'to', header: t('m39.synonyms.colTo'), render: (r) => r.toTerm },
              { key: 'type', header: t('m39.common.type'), render: (r) => r.synonymType },
              { key: 'bidirectional', header: t('m39.synonyms.bidirectional'), render: (r) => r.isBidirectional ? t('common.yes') : t('common.no') },
              { key: 'lang', header: t('m39.synonyms.colLang'), render: (r) => r.languageCode },
              { key: 'weight', header: t('m39.synonyms.weight'), render: (r) => decimal(r.weight) },
              { key: 'active', header: t('m39.common.active'), render: (r) => r.isActive ? t('common.yes') : t('common.no') },
              { key: 'actions', header: t('m39.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`toggle-${r.fromTerm}`} onClick={() => toggleActive(r)}>
                    {r.isActive ? t('m39.common.disable') : t('m39.common.enable')}
                  </Button>
                  <Button variant="danger" type="button" aria-label={`delete-${r.fromTerm}`} onClick={() => remove(r)}>{t('common.delete')}</Button>
                </>
              ) },
            ]}
          />
        )}
    </main>
  );
}
